// React
import React, { Fragment, useState, useEffect } from 'react'

// Components
import { ActionButton, Carousel } from 'components'
import { Card, CardBody } from 'reactstrap'

// Icons
import { FaCheckSquare, FaExclamationCircle } from 'react-icons/fa'

// Third-Party Library
import styled from 'styled-components'
import PropTypes from "prop-types"
import { dateConvert } from 'functions'

const Day = styled.div`
  width: 14.2%;
  cursor: pointer;
`

const FullCalendar = props => {
  // Variables
  const today = new Date()
  const dateSelected = new Date(props.value)
  const DAYS = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31]
  const DAYS_LEAP = [31, 29, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31]
  const DAYS_OF_THE_WEEK = ['MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT', 'SUN']

  // States
  const [date, setDate] = useState(dateSelected)
  const [dateHovered, setDateHovered] = useState(today)
  const [month, setMonth] = useState(date.getMonth())
  const [year, setYear] = useState(date.getFullYear())
  const [startDay, setStartDay] = useState(getStartDayOfMonth(date))

  useEffect(() => {
    setMonth(date.getMonth())
    setYear(date.getFullYear())
    setStartDay(getStartDayOfMonth(date))
  }, [date])

  function getStartDayOfMonth(date) {
    const startDate = new Date(date.getFullYear(), date.getMonth(), 1).getDay()
    return startDate === 0 ? 7 : startDate
  }

  function isLeapYear(year) {
    return (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0
  }

  // Variables
  const days = isLeapYear(year) ? DAYS_LEAP : DAYS
  const countDays = days[date.getMonth()] + (startDay - 1)
  const remainingDays = 7 - (countDays % 7)

  const SelectedDate = () => (
    <div className="position-absolute top-50 start-50 translate-middle text-center text-primary2 mt-3" style={{ width: '95%' }}>
      <FaCheckSquare size={20} />
      <div className='text-center text-uppercase d-sm-block d-none' style={{ fontSize: '.625rem' }}>Selected Date</div>
    </div>
  )

  const HoveredDate = () => (
    <div className="position-absolute bottom-0 start-50 translate-middle-x text-center text-primary2" style={{ width: '95%' }}>
      <div className='d-flex flex-lg-row flex-column text-uppercase justify-content-center'>
        <div className='px-1' style={{ fontSize: '.625rem' }}>Choose</div>
        <span className='d-md-block d-none' style={{ fontSize: '.625rem' }}>This Date</span>
      </div>
    </div>
  )

  const getMonthDifference = () => {
    if (props.maxDate && today <= props.maxDate) {
      const data = (props.maxDate.getMonth() - today.getMonth() + (12 * (props.maxDate.getFullYear() - today.getFullYear()))) + 1

      return data
    } else {
      return 10
    }
  }

  return (
    <Fragment>
      <Carousel>
        {[...Array(getMonthDifference())].map((_val, index) => {
          // Variables
          const base = new Date()
          const data = new Date(base.getFullYear(), base.getMonth(), 1)
          const value = data.setMonth(data.getMonth() + index)
          const selectedMonth = Boolean(date.getMonth() === data.getMonth() && date.getFullYear() === data.getFullYear())

          return (
            <Card key={index} color={`${selectedMonth && "primary"}`} outline={selectedMonth} style={{ cursor: 'pointer' }} onClick={() => setDate(data)}>
              <CardBody className="py-0 text-center" style={{ userSelect: 'none' }}>
                <small className={`${selectedMonth && "text-primary"}`}>{new Date(value).toLocaleDateString('en-US', { month: 'short' })}</small>
                <div className={`fw-bold ${selectedMonth && "text-primary"}`}>{new Date(value).toLocaleDateString('en-US', { year: 'numeric' })}</div>
              </CardBody>
            </Card>
          )
        })}
      </Carousel>

      <h3 className='text-center'>
        {date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
      </h3>

      <div className='d-flex flex-wrap'>
        {DAYS_OF_THE_WEEK.map((d, index) => (
          <Day key={d} className={`text-center py-1 border-top border-bottom ${index === 0 && "border-start"} ${index === DAYS_OF_THE_WEEK.length - 1 && "border-end"}`}>
            <strong>{d}</strong>
          </Day>
        ))}

        {Array(countDays).fill(null).map((_, index) => {
          // Variables
          const d = index - (startDay - 2)
          const todayDate = new Date(today.setHours(0, 0, 0, 0))
          const thisDate = new Date(year, month, d)
          const pastDate = thisDate < todayDate
          const fillBorder = new Date(thisDate).setDate(thisDate.getDate() + (7 - (index % 7))) > today
          const isToday = String(thisDate) === String(todayDate)
          const isSelected = dateConvert(thisDate).default.format === dateConvert(dateSelected).default.format
          const isHovered = String(thisDate) === String(dateHovered)
          const isEvent = props.events.find(val => {
            // Variables
            const date = new Date(val.date)
            date.setHours(0, 0, 0, 0)

            return String(thisDate) === String(date)
          })

          return (
            <Day
              key={index}
              className={`${Boolean(!pastDate || fillBorder) && "border"} position-relative`}
              style={{ height: Boolean(!pastDate || fillBorder) ? 120 : 0 }}
              onClick={() => {
                if (d > 0 && !pastDate) {
                  if (isEvent) {
                    if (isEvent.url) {
                      window.open(isEvent.url, '_blank')
                    }
                  } else {
                    props.onClick(new Date(year, month, d))
                  }
                }
              }}
              onMouseEnter={() => {
                if (d > 0 && !pastDate) {
                  setDateHovered(new Date(year, month, d))
                }
              }}
            >
              {d > 0 ? (
                <Fragment>
                  <div className="position-absolute top-0 start-0" style={{ width: '100%' }}>
                    <div className='m-2 d-flex flex-md-row flex-column'>
                      {!pastDate && (
                        <Fragment>
                          <div className='pe-1'>{d}</div>
                          <div>{` ${date.toLocaleDateString('en-US', { month: 'short' })}`}</div>
                        </Fragment>
                      )}
                    </div>
                  </div>

                  <section>
                    {isEvent ? (
                      <div className="position-absolute bottom-0 start-50 translate-middle-x" style={{ width: '95%' }}>
                        <ActionButton block outline type="button" color='danger'>
                          <div className='d-flex align-items-center justify-content-center'>
                            <span className='text-uppercase align-middle ms-25 pe-1 d-lg-block d-none' style={{ fontSize: '.625rem' }}>{isEvent.label}</span>
                            <FaExclamationCircle size={20} />
                          </div>
                        </ActionButton>
                      </div>
                    ) : isToday && (
                      <div className='d-lg-block d-none'>
                        <div className="position-absolute bottom-0 start-50 translate-middle-x" style={{ width: '95%' }}>
                          <ActionButton block outline type="button" color='primary2' className="d-lg-block d-none">
                            <div className='d-flex align-items-center justify-content-center'>
                              <span className='text-uppercase align-middle ms-25 pe-1' style={{ fontSize: '.625rem' }}>Choose Today </span>
                              <FaCheckSquare size={20} />
                            </div>
                          </ActionButton>
                        </div>
                      </div>
                    )}

                    <div className='d-block d-lg-none'>
                      {isSelected && !isEvent ? <SelectedDate /> : isHovered && !isEvent && <HoveredDate />}
                    </div>
                  </section>

                  {isSelected && !isToday && !isEvent ? <SelectedDate /> : isHovered && !isToday && !isEvent && <HoveredDate />}
                </Fragment>
              ) : <div />}
            </Day>
          )
        })}

        {remainingDays && [...Array(remainingDays)].map((_, index) => {
          return (
            <Day
              key={index}
              className='border position-relative'
              style={{
                height: 120
              }}
            />
          )}
        )}
      </div>
    </Fragment>
  )
}

FullCalendar.propTypes = {
	value: PropTypes.string,
  onClick: PropTypes.func,
  maxDate: PropTypes.instanceOf(Date),
	events: PropTypes.arrayOf(
		PropTypes.shape({
      date: PropTypes.string.isRequired,
      url: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired
		})
	)
}

FullCalendar.defaultProps = {
	events: []
}

export { FullCalendar }